import { useState } from "react";

import type { BancoOut } from "../../lib/cadastros/tipos";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Formulário controlado de banco do catálogo global (FE-4 CP-C). Cobre os DOIS
 * modos:
 *
 *  - CRIAÇÃO (`banco === undefined`): `codigo_compe` + `nome`.
 *  - EDIÇÃO (`banco` presente): `codigo_compe` READ-ONLY (chave do catálogo COMPE,
 *    não editável no PATCH do backend) + `nome`.
 *
 * Banco é catálogo GLOBAL: o backend exige escopo IRRESTRITO para criar/editar
 * (devolve 403 a usuário escopado). O gating de ABRIR este formulário é do
 * orquestrador (`PaginaBancos`, via `useTemPermissaoIrrestrita`); aqui só tratamos
 * o 403 como defesa-em-profundidade caso escape.
 *
 * Componente PURO de apresentação/coleta: não conhece o hook de mutação. O
 * orquestrador injeta `aoSalvar`, o estado `salvando` e o `erro` normalizado.
 * Espelha o estilo de `FormularioEmpresa`/`FormularioConta`.
 *
 * SÓ tokens CPMH; labels em todos os inputs; erro com `role="alert"`.
 */

/** Campos coletados. Na edição o `codigo_compe` é ignorado pelo caller. */
export interface DadosBancoForm {
  codigo_compe: string;
  nome: string;
}

interface FormularioBancoProps {
  /** Banco em edição, ou `undefined` para criação. */
  banco?: BancoOut;
  aoSalvar: (dados: DadosBancoForm) => void;
  aoCancelar: () => void;
  salvando: boolean;
  erro: unknown;
}

export function FormularioBanco({
  banco,
  aoSalvar,
  aoCancelar,
  salvando,
  erro,
}: FormularioBancoProps): JSX.Element {
  const ehEdicao = banco !== undefined;

  const [codigoCompe, setCodigoCompe] = useState(banco?.codigo_compe ?? "");
  const [nome, setNome] = useState(banco?.nome ?? "");
  const [erroBorda, setErroBorda] = useState<string | null>(null);

  function submeter(): void {
    // Validação de borda: campos obrigatórios na criação.
    if (!ehEdicao && codigoCompe.trim() === "") {
      setErroBorda("Informe o código COMPE.");
      return;
    }
    if (nome.trim() === "") {
      setErroBorda("Informe o nome do banco.");
      return;
    }
    setErroBorda(null);
    aoSalvar({
      codigo_compe: codigoCompe.trim(),
      nome: nome.trim(),
    });
  }

  return (
    <div className="flex max-w-lg flex-col gap-4 rounded-md border border-platinum bg-snow p-6 font-corpo text-base text-off-black">
      <h2 className="font-titulo text-xl text-off-black">
        {ehEdicao ? "Editar banco" : "Novo banco"}
      </h2>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">Código COMPE</span>
        <input
          type="text"
          value={codigoCompe}
          onChange={(evento) => setCodigoCompe(evento.target.value)}
          readOnly={ehEdicao}
          aria-readonly={ehEdicao}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black read-only:bg-platinum"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">Nome</span>
        <input
          type="text"
          value={nome}
          onChange={(evento) => setNome(evento.target.value)}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
        />
      </label>

      {erroBorda !== null && (
        <p role="alert" className="font-corpo text-base text-flame">
          {erroBorda}
        </p>
      )}

      {erro !== null && erro !== undefined && (
        <p role="alert" className="font-corpo text-base text-flame">
          {mensagemErroBanco(erro)}
        </p>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={aoCancelar}
          disabled={salvando}
          className="rounded-md border border-platinum bg-snow px-4 py-1 text-off-black disabled:opacity-60"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={submeter}
          disabled={salvando}
          className="rounded-md border border-flame bg-flame px-4 py-1 text-snow disabled:opacity-60"
        >
          {salvando ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </div>
  );
}

/**
 * Traduz o erro de uma mutação de banco numa mensagem estável. 409 (código COMPE
 * duplicado), 403 (sem escopo global para o catálogo) e 422 (validação) recebem
 * mensagens claras; demais reusam a mensagem genérica do `ErroHttp`. Erro
 * não-`ErroHttp` → fallback genérico.
 */
function mensagemErroBanco(erro: unknown): string {
  if (erro instanceof ErroHttp) {
    if (erro.status === 409) {
      return "Já existe um banco com este código COMPE.";
    }
    if (erro.status === 403) {
      return "Sem permissão para alterar o catálogo global de bancos.";
    }
    if (erro.status === 422) {
      return "Dados inválidos. Verifique os campos e tente novamente.";
    }
    return erro.message;
  }
  return "Não foi possível salvar o banco.";
}
